import { addDays, startOfWeek, type WeekStart } from '@awwyeah/shared';

/**
 * Date maths for the week view's horizontal carousel.
 *
 * Usability testing 2026-08-01 (Faye, iPhone): paging between weeks jumped a whole screen
 * with no motion — "I want to be able to smoothly scroll the weekly view". The view now
 * renders three panes (previous / current / next) inside a scroll-snap track: the browser
 * supplies the momentum, and once a snap settles the anchor moves by a week and the track
 * silently recentres — the standard infinite-carousel trick. Everything here is pure so
 * the recentring maths can be tested without a browser.
 */

/** The middle pane is the one on screen at rest. */
export const CENTER_PANE = 1;
export const PANE_COUNT = 3;

/** `[previous week, current week, next week]`, each normalised to its week start. */
export function weekPanes(anchor: Date, weekStartsOn: WeekStart): Date[] {
  const start = startOfWeek(anchor, weekStartsOn);
  return [addDays(start, -7), start, addDays(start, 7)];
}

/** Which pane a scroll offset has settled on. */
export function paneIndexFor(scrollLeft: number, paneWidth: number): number {
  if (paneWidth <= 0) return CENTER_PANE;
  const index = Math.round(scrollLeft / paneWidth);
  return Math.min(PANE_COUNT - 1, Math.max(0, index));
}

/** Weeks to shift the anchor by when the track settles on `paneIndex`. */
export function shiftForPane(paneIndex: number): -1 | 0 | 1 {
  if (paneIndex < CENTER_PANE) return -1;
  if (paneIndex > CENTER_PANE) return 1;
  return 0;
}

/** Where the track must sit, without animation, for `paneIndex` to be on screen. */
export function offsetForPane(paneIndex: number, paneWidth: number): number {
  return paneIndex * paneWidth;
}

export type AnchorChange = 'none' | 'next' | 'prev' | 'jump';

/**
 * How the anchor moved, so the view can decide between "slide one pane" and "just be
 * there": prev/next animate, an arbitrary jump (Today, a month-view tap) does not.
 */
export function anchorChange(from: Date, to: Date, weekStartsOn: WeekStart): AnchorChange {
  const fromStart = startOfWeek(from, weekStartsOn).getTime();
  const toStart = startOfWeek(to, weekStartsOn).getTime();
  if (fromStart === toStart) return 'none';
  if (addDays(new Date(fromStart), 7).getTime() === toStart) return 'next';
  if (addDays(new Date(fromStart), -7).getTime() === toStart) return 'prev';
  return 'jump';
}

/**
 * Pane the track should start from so that animating back to centre reads as movement in
 * the right direction: stepping to the *next* week starts on the left pane (the week you
 * just left) and slides left.
 */
export function entryPaneFor(change: AnchorChange): number {
  if (change === 'next') return CENTER_PANE - 1;
  if (change === 'prev') return CENTER_PANE + 1;
  return CENTER_PANE;
}
